import React from "react";
import { Box, Button, TextField, useTheme } from "@mui/material";
import { Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  selectColumns,
  selectColumnOrder,
  selectBoardState,
  selectGroupingConfig,
  selectFilters,
  selectLoadingStatuses,
  selectHasMoreMap,
  selectActiveSprintIds,
  selectSearchQuery,
} from "@/features/bughurd/boardSelector";
import { setIsAddingColumn, setNewColumnTitle } from "@/features/bughurd/slice";
import { AutoScrollContainer } from "./AutoScrollContainer";
import SortableColumn from "./SortableColumn"; // New component for sortable columns
import Column from "./Column"; // Regular column for non-sortable cases
import { useColumnOperations } from "./custom-hooks/useColumnOperations";
import { useColumnDragAndDrop } from "./custom-hooks/useDragAndDrop"; // New hook
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useParams } from "react-router-dom";

interface KanbanBoardProps {
  handleTaskUpdate: (
    taskId: string,
    updatedTask: any | null,
    newColumnId?: string
  ) => void;
  refreshColumnData: (columnId?: string, subGroupId?: string) => Promise<void>;
  handleLoadMore: (columnId: string, subGroupId?: string) => void;
  isMyTaskView: boolean;
  reactFlow?: any;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  handleTaskUpdate,
  refreshColumnData,
  handleLoadMore,
  isMyTaskView,
  reactFlow,
}) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const columns = useAppSelector(selectColumns);
  const columnOrder = useAppSelector(selectColumnOrder);
  const boardState = useAppSelector(selectBoardState);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const filters = useAppSelector(selectFilters);
  const searchQuery = useAppSelector(selectSearchQuery);
  const loadingStatuses = useAppSelector(selectLoadingStatuses);
  const hasMoreMap = useAppSelector(selectHasMoreMap);
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal
  );

  const { minimizedColumns, isAddingColumn, newColumnTitle, projectDetails } =
    boardState;
  const { groupBy, subGroupBy } = groupingConfig;
  const {
    selectedStatuses,
    selectedAvatars,
    selectedPriorities,
    selectedTags,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedCreatedBy,
  } = filters;
  const boardType = projectDetails?.boardType;

  const hasFilters =
    (searchQuery && searchQuery.trim().length > 0) ||
    selectedAvatars?.length > 0 ||
    selectedTags?.length > 0 ||
    selectedStatuses?.length > 0 ||
    selectedPriorities?.length > 0 ||
    selectedProjects?.length > 0 ||
    selectedCreatedBy?.length > 0 ||
    dueDateStart ||
    dueDateEnd ||
    startDateStart ||
    startDateEnd ||
    updatedAtStart ||
    updatedAtEnd ||
    isRecurring;
  const activeSprintIds = useAppSelector(selectActiveSprintIds);

  const {
    addColumn,
    handleColumnTitleChange,
    handleColumnDeleteRequest,
    isCreatingStatus,
  } = useColumnOperations(id, isMyTaskView);

  const { handleColumnDragEnd } = useColumnDragAndDrop();

  // Sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum distance before drag starts
        delay: 0,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Check if columns can be reordered
  const canReorderColumns =
    groupBy === "Status" && !isMyTaskView && subGroupBy === "None";

  // Filter visible columns
  const getVisibleColumns = () => {
    return columnOrder.filter((columnId) => {
      const column = columns[columnId];
      const columnLoadingState = loadingStatuses[columnId] || {
        isInitialLoading: false,
        isLoadingMore: false,
      };

      if (column?.hidden) return false;

      if (
        !column ||
        (groupBy === "Assignee" &&
          !columnLoadingState.isInitialLoading &&
          column?.tasks?.length === 0)
      ) {
        return false;
      }

      // Additional condition for Status grouping with isMyTaskView
      if (
        groupBy === "Status" &&
        isMyTaskView &&
        !columnLoadingState.isInitialLoading &&
        column?.tasks?.length === 0
      ) {
        return false;
      }

      if (
        groupBy === "Status" &&
        selectedStatuses.length > 0 &&
        !selectedStatuses.includes(columnId)
      ) {
        return false;
      }

      if (groupBy === "Assignee" && selectedAvatars.length > 0) {
        if (!selectedAvatars.includes(column?.id)) {
          return false;
        }
      }

      if (
        groupBy === "Priority" &&
        selectedPriorities.length > 0 &&
        !selectedPriorities.includes(column?.title)
      ) {
        return false;
      }

      if (groupBy === "Tags" && selectedTags.length > 0) {
        if (!selectedTags.includes(column?.title)) {
          return false;
        }
      }

      return true;
    });
  };

  const visibleColumns = getVisibleColumns();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    handleColumnDragEnd(active.id as string, over.id as string);
  };

  const renderColumn = (columnId: string, index: number) => {
    const column = columns[columnId];
    const columnLoadingState = loadingStatuses[columnId] || {
      isInitialLoading: false,
      isLoadingMore: false,
    };
    const isMinimized = minimizedColumns[columnId] || false;

    let commonProps = {
      key: columnId,
      column,
      noSprintTask: false,
      tasks: column.tasks || [],
      onTitleChange: (newTitle: string) =>
        handleColumnTitleChange(columnId, newTitle),
      onDelete: () => handleColumnDeleteRequest(columnId),
      isInitialLoading: columnLoadingState.isInitialLoading,
      isLoadingMore: columnLoadingState.isLoadingMore,
      refreshData: () => refreshColumnData(columnId),
      onLoadMore: () => handleLoadMore(columnId),
      hasMore: hasMoreMap[columnId],
      onTaskUpdate: handleTaskUpdate,
      canEditColumn: groupBy === "Status" && !isMyTaskView,
      style: {
        width: isMinimized ? "40px" : "clamp(280px, calc(90vw / 5), 400px)",
        minWidth: isMinimized ? "40px" : "280px",
        maxWidth: isMinimized ? "40px" : "400px",
        transition: "width 0.3s ease, min-width 0.3s ease",
        flexShrink: 0,
      },
    };

    if (
      index === 0 &&
      boardType === "SCRUM" &&
      activeSprintIds?.length === 0 &&
      column?.tasks?.length === 0 &&
      boardState?.selectedSprintData?.status !== "ARCHIVE"
    ) {
      commonProps = {
        ...commonProps,
        noSprintTask: true,
      };
    }

    if (canReorderColumns) {
      return <SortableColumn {...commonProps} id={columnId} />;
    }

    return (
      <div key={columnId} style={commonProps.style} data-column-id={columnId}>
        <Column {...commonProps} reactFlow={reactFlow} />
      </div>
    );
  };

  const content = (
    <AutoScrollContainer
      style={{
        display: "flex",
        height: hasFilters ? "calc(100vh - 230px)" : "calc(100vh - 190px)",
        gap: "clamp(12px, 1.5vw, 24px)",
      }}
      className="kanban-container"
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        p: 1,
        borderRadius: 4,
        backgroundColor:
          theme.palette.mode === "dark" ? "#1e2430" : "#f2f6f8",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "clamp(12px, 1vw, 24px)",
          width: "max-content",
          minWidth: "100%",
          pointerEvents: "auto",
          height: "100%",
        }}
      >
        {visibleColumns.map((columnId, index) => renderColumn(columnId, index))}

        {/* Move the Add Column button inside the columns container */}
        {groupBy === "Status" && !isMyTaskView && (
          <>
            {!isAddingColumn && !DEFEKT_TRACKER.HIDE_CREATE(myPermissions) ? (
              <Button
                onClick={() => dispatch(setIsAddingColumn(true))}
                sx={{
                  minWidth: "clamp(280px, calc(90vw / 5), 400px)", // Match column width
                  width: "clamp(280px, calc(90vw / 5), 400px)", // Match column width
                  height: "fit-content",
                  color: "text.secondary",
                  textTransform: "none",
                  fontSize: "13px",
                  minHeight: "27px",
                  p: 1,
                  flexShrink: 0,
                }}
                variant="outlined"
                startIcon={<Add />}
              >
                {t("Add another column")}
              </Button>
            ) : (
              <Box
                sx={{
                  minWidth: "clamp(280px, calc(90vw / 5), 400px)", // Match column width
                  width: "clamp(280px, calc(90vw / 5), 400px)", // Match column width
                  backgroundColor: "white",
                  borderRadius: 1,
                  p: 1,
                  flexShrink: 0,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  value={newColumnTitle}
                  onChange={(e) => dispatch(setNewColumnTitle(e.target.value))}
                  placeholder={t("Enter column title...")}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      addColumn();
                    }
                  }}
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <LoadingButton
                    variant="contained"
                    size="small"
                    onClick={addColumn}
                    loading={isCreatingStatus}
                  >
                    {t("Add Column")}
                  </LoadingButton>
                  <Button
                    size="small"
                    onClick={() => {
                      dispatch(setIsAddingColumn(false));
                      dispatch(setNewColumnTitle(""));
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </AutoScrollContainer>
  );

  if (canReorderColumns) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        // Add this to prevent interference
        onDragStart={(event) => {
          // Only allow column drag if it's actually a column being dragged
          if (event.active.data.current?.type !== "column") {
            return false;
          }
        }}
      >
        <SortableContext
          items={visibleColumns}
          strategy={horizontalListSortingStrategy}
        >
          {content}
        </SortableContext>
      </DndContext>
    );
  }

  return content;
};

export default KanbanBoard;
